export type RequestCacheOptions = {
  ttlMs: number;
};

export type RequestCache<T> = {
  get: (loader: () => Promise<T>, forceRefresh?: boolean) => Promise<T>;
  invalidate: () => void;
};

export type KeyedRequestCache<K, T> = {
  get: (key: K, loader: () => Promise<T>, forceRefresh?: boolean) => Promise<T>;
  invalidate: (key?: K) => void;
};

type RequestCacheState<T> = {
  cached: { expiresAt: number; value: T } | null;
  promise: Promise<T> | null;
  version: number;
};

const requestCacheInvalidators = new Set<() => void>();

export function invalidateAllRequestCaches() {
  requestCacheInvalidators.forEach((invalidate) => invalidate());
}

/** Coalesces concurrent reads and keeps successful read-only data briefly reusable. */
export function createRequestCache<T>({ ttlMs }: RequestCacheOptions): RequestCache<T> {
  const state: RequestCacheState<T> = { cached: null, promise: null, version: 0 };
  requestCacheInvalidators.add(() => {
    state.version += 1;
    state.cached = null;
  });

  return {
    get(loader, forceRefresh = false) {
      if (!forceRefresh && state.cached && state.cached.expiresAt > Date.now()) {
        return Promise.resolve(state.cached.value);
      }
      if (state.promise) {
        return state.promise;
      }

      const requestVersion = state.version;
      const promise = (async () => {
        try {
          const value = await loader();
          if (state.version === requestVersion) {
            state.cached = { expiresAt: Date.now() + ttlMs, value };
          }
          return value;
        } catch (error) {
          state.cached = null;
          throw error;
        } finally {
          state.promise = null;
        }
      })();
      state.promise = promise;
      return promise;
    },
    invalidate() {
      state.version += 1;
      state.cached = null;
    },
  };
}

export function createKeyedRequestCache<K, T>({
  ttlMs,
}: RequestCacheOptions): KeyedRequestCache<K, T> {
  const caches = new Map<K, RequestCache<T>>();
  const getCache = (key: K) => {
    let cache = caches.get(key);
    if (!cache) {
      cache = createRequestCache<T>({ ttlMs });
      caches.set(key, cache);
    }
    return cache;
  };

  return {
    get: (key, loader, forceRefresh = false) => getCache(key).get(loader, forceRefresh),
    invalidate(key) {
      if (key === undefined) {
        caches.forEach((cache) => cache.invalidate());
      } else {
        caches.get(key)?.invalidate();
      }
    },
  };
}
